import AddIcon from "@assets/icons/plus.svg";
import XIcon from "@assets/icons/x-circle.svg";

import useSystemStore from "@store/useSystemStore";
import { CompanyAvatar, CompanyAvatarSizes } from "@ui/Avatars";
import { IPeople } from "@interfaces/people.interface";
import { cn } from "@utils/tailwind.util";
import { Tooltip } from "@ui/Tooltip";

export interface IPersonListItemProps {
  person: IPeople;
  erasable?: boolean;
  compactErasable?: boolean;
  onDelete?: () => void;
  size?: "small" | "medium";
  onClick?: () => void;
  displayNameClassName?: string;
  isSuggested?: boolean;
  containerClassName?: string;
}

export const OFFSET_PADDING = 31;

export function PersonListItem({
  person,
  erasable,
  compactErasable,
  onDelete,
  size = "small",
  onClick,
  displayNameClassName,
  isSuggested,
  containerClassName,
}: IPersonListItemProps) {
  const { isDarkMode } = useSystemStore();

  const displayName =
    person.first_name && person.last_name
      ? `${person.first_name} ${person.last_name}`
      : person.email;
  const canShowCompanyLogo = isSuggested
    ? person.primary_company?.company?.is_suggested === false
    : true;
  const iconSize = size === "small" ? 20 : 24;

  return (
    <div
      className={cn(
        "group relative flex h-7 w-full max-w-full items-center justify-between rounded-md p-2",
        "text-content-primary dark:text-white dark:bg-gray-750 dark:ring-gray-700 dark:hover:bg-gray-750",
        {
          "hover:bg-background-clear": erasable && !compactErasable,
          // NOTE: website uses `bg-background-tag` but it's displayed too light in the add-in, had to change to `bg-gray-200`
          "w-fit bg-gray-100 p-0.5 pr-1": !erasable || (erasable && compactErasable),
          "!px-1 !py-1 !pr-1.5": size === "medium",
          "cursor-pointer": onClick,
          "border border-dashed border-stroke-secondary bg-transparent dark:bg-transparent transition-colors duration-200 hover:border-content-disabled hover:bg-gray-100 ring-0":
            isSuggested && !erasable,
          "h-auto": erasable,
        },
        containerClassName
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <div className="flex items-center min-w-0">
        <div className={cn("flex-shrink-0", { "mr-2": erasable, "mr-1": !erasable })}>
          <CompanyAvatar
            size={size === "small" ? CompanyAvatarSizes.SMALL : CompanyAvatarSizes.BIG}
            logoUrl={
              person.primary_company?.company?.is_suggested
                ? undefined
                : person.primary_company?.company?.logo_url
            }
            canShowCompanyLogo={canShowCompanyLogo}
            userProps={person}
            classNames={cn("border !rounded-full", {
              "!w-4 !h-4 text-[10px] flex-shrink-0 border-none mx-0": !erasable,
              "!w-4 !h-4": size === "small",
              "!w-5 !h-5": size === "medium",
            })}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1 min-w-0">
            <Tooltip
              variant="dark"
              placement="left"
              tooltipContent={displayName}
              arrow
              className="!z-0 grid"
              dependsOnTruncated
              tooltipContentClassName="-mt-[0.5px]"
              offset={OFFSET_PADDING}
            >
              <div className="flex items-center gap-1 min-w-0">
                <div
                  className={cn("block truncate font-medium", {
                    "text-sm": size === "medium",
                    "text-xs": size === "small",
                    displayNameClassName,
                  })}
                >
                  {displayName}
                </div>
                {isSuggested && erasable && (
                  <div className="h-full shrink-0 items-center rounded-[5px] border border-dashed border-stroke-secondary bg-white px-[5px]">
                    <p className="text-xs font-medium text-content-secondary">Not in Meridian</p>
                  </div>
                )}
              </div>
            </Tooltip>
            {erasable && person.primary_company ? (
              <div className="flex min-w-0">
                <span
                  className={cn("block truncate text-gray-450 dark:text-gray-300", {
                    "text-xs": size === "small",
                    "text-sm": size === "medium",
                  })}
                >
                  {person.primary_company.company.title}

                  {person.primary_company.relation && person.primary_company.relation.length > 0 ? (
                    <span
                      className={cn("ml-1 text-gray-450 dark:text-gray-300", {
                        "text-xs": size === "small",
                        "text-sm": size === "medium",
                      })}
                    >
                      • {person.primary_company.relation}
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
          </div>
          {erasable && (
            <span
              className={cn("block truncate text-teal-600 dark:text-teal-300 underline", {
                "text-xs": size === "small",
                "text-sm": size === "medium",
              })}
            >
              {person.email}
            </span>
          )}
        </div>
      </div>

      {isSuggested && !erasable && (
        <div
          className="pointer-events-none absolute right-0 top-0 flex h-full w-12 items-center justify-end rounded-md opacity-0 duration-200 group-hover:opacity-100"
          style={{
            background: isDarkMode
              ? "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #313643) 50.41%)"
              : "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #F3F4F6) 50.41%)",
          }}
        >
          <AddIcon
            className="mr-1 text-content-secondary dark:text-white pointer-events-auto"
            stroke="2.5"
            width={20}
            height={20}
          />
        </div>
      )}

      {erasable && (
        <>
          <XIcon
            width={iconSize}
            height={iconSize}
            className={cn(
              "invisible flex-shrink-0 cursor-pointer p-0 pr-1 text-gray-450 dark:text-invertedPrimary80 group-hover:visible",
              {
                "absolute bottom-0 right-1 top-0 z-20 flex h-full w-4 items-center justify-end rounded p-0 leading-[0] opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100":
                  compactErasable,
              }
            )}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          />

          {compactErasable ? (
            <div
              className="pointer-events-none absolute right-0 top-0 z-10 h-full w-10 rounded opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100"
              style={{
                background: isDarkMode
                  ? "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #313643) 50.41%)"
                  : "linear-gradient(90deg, rgba(229, 231, 235, 0.00) 0%, var(--tag-hover, #F3F4F6) 50.41%)",
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
